import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Box,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

interface FAQ {
  question: string;
  answer: string;
  category: string;
  relatedArticles?: Array<{
    title: string;
    link: string;
  }>;
}

const FAQPage: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const faqs: FAQ[] = [
    {
      question: "What is the Amsler Grid?",
      answer: "The Amsler Grid is a tool used to detect vision problems, particularly those affecting the central visual field. It consists of a grid pattern with a central fixation point.",
      category: "General",
      relatedArticles: [
        { title: "Understanding the Amsler Grid", link: "/docs/user-guide#understanding" },
        { title: "History of Vision Testing", link: "/docs/articles/vision-testing" }
      ]
    },
    {
      question: "How often should I test my vision?",
      answer: "It's recommended to test your vision daily, especially if you have been diagnosed with macular degeneration or other eye conditions that affect central vision.",
      category: "Testing",
      relatedArticles: [
        { title: "Daily Testing Guide", link: "/docs/user-guide#daily-testing" },
        { title: "Vision Monitoring Best Practices", link: "/docs/articles/best-practices" }
      ]
    },
    {
      question: "What should I do if I notice changes in the grid?",
      answer: "If you notice any changes in how the grid appears, such as wavy lines, missing areas, or distorted patterns, contact your eye care professional immediately.",
      category: "Health",
      relatedArticles: [
        { title: "Understanding Vision Changes", link: "/docs/articles/vision-changes" },
        { title: "When to See a Doctor", link: "/docs/articles/medical-advice" }
      ]
    },
    {
      question: "How do I export my test results?",
      answer: "You can export your test results in PDF or CSV format from the Test Results page. Click on the Export button and choose your preferred format.",
      category: "Features",
      relatedArticles: [
        { title: "Managing Test Results", link: "/docs/user-guide#managing-results" },
        { title: "Data Export Guide", link: "/docs/articles/data-export" }
      ]
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we take data security seriously. All your data is encrypted and stored securely in compliance with healthcare privacy regulations.",
      category: "Privacy",
      relatedArticles: [
        { title: "Privacy Policy", link: "/privacy-policy" },
        { title: "Data Security Measures", link: "/docs/articles/security" }
      ]
    },
    {
      question: "Can I use the app offline?",
      answer: "Yes, the app works offline. Your test results will be synchronized with our servers when you're back online.",
      category: "Features",
      relatedArticles: [
        { title: "Offline Usage Guide", link: "/docs/user-guide#offline-use" },
        { title: "Data Synchronization", link: "/docs/articles/sync" }
      ]
    }
  ];

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  const filteredFAQs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesSearch = searchQuery.trim() === '' ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = !selectedCategory || faq.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Frequently Asked Questions
        </Typography>

        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                sx={{ '&:hover': { bgcolor: 'action.hover' } }}
              />
            ))}
          </Box>
        </Box>

        {filteredFAQs.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No FAQs found matching your search criteria.
          </Alert>
        ) : (
          filteredFAQs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>{faq.answer}</Typography>
                
                {faq.relatedArticles && faq.relatedArticles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Related Articles:
                    </Typography>
                    <List dense>
                      {faq.relatedArticles.map((article, idx) => (
                        <ListItem key={idx} disablePadding>
                          <ListItemText>
                            <Link
                              component={RouterLink}
                              to={article.link}
                              color="primary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                              <ArticleIcon fontSize="small" />
                              {article.title}
                            </Link>
                          </ListItemText>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        <Alert severity="info" sx={{ mt: 4 }}>
          Can't find what you're looking for? Check our{' '}
          <Link component={RouterLink} to="/docs/user-guide">User Guide</Link> or{' '}
          <Typography variant="body1" paragraph>
            If you have a question that's not answered here, please <Link href="mailto:support@kubomaster.llc">contact our support team</Link>.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default FAQPage; 